import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import openaiService from '../services/openaiService';

const { FiSearch, FiMessageSquare, FiDollarSign, FiTrendingUp, FiExternalLink } = FiIcons;

function AIVehicleFinder() {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [aiRecommendation, setAiRecommendation] = useState('');

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setIsSearching(true);
    setSearchResults(null);
    setAiRecommendation('');

    try {
      // Get AI recommendations based on search query
      const aiResponse = await openaiService.getVehicleRecommendations({
        query: searchQuery,
        preferences: 'Customer search requirements'
      });

      setAiRecommendation(aiResponse);

      // Simulate vehicle results (in real app, this would come from your inventory API)
      const mockResults = [
        {
          id: 1,
          make: 'Audi',
          model: 'Q5 Quattro',
          year: 2022,
          price: 42999,
          mileage: 28500,
          image: 'https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=400&h=300&fit=crop',
          features: ['Quattro AWD', 'Premium Plus', 'Navigation', 'Safety Package'],
          aiMatch: 95
        },
        {
          id: 2,
          make: 'BMW',
          model: 'X3 xDrive',
          year: 2023,
          price: 44999,
          mileage: 15200,
          image: 'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=400&h=300&fit=crop',
          features: ['xDrive AWD', 'M Sport Package', 'Premium Audio'],
          aiMatch: 92
        }
      ];

      setTimeout(() => {
        setSearchResults(mockResults);
        setIsSearching(false);
      }, 2000);

    } catch (error) {
      console.error('Search error:', error);
      setIsSearching(false);
      setAiRecommendation("I'm having trouble processing your search right now. Please try again or visit shawnryder.com for more information.");
    }
  };

  const popularSearches = [
    'Luxury SUV with latest tech under $50K',
    'Fuel-efficient sedan for daily commuting',
    'Family-friendly vehicles with safety features',
    'Electric vehicles with long range'
  ];

  return (
    <section id="vehicle-finder" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            AI Vehicle Finder
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Just tell us what you're looking for in plain English. Our advanced AI will find the perfect matches.
          </p>
          <div className="mt-4">
            <a 
              href="https://shawnryder.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-1 text-blue-600 hover:text-blue-700 transition-colors"
            >
              <span className="text-sm">Powered by Shawn Ryder Digital AI</span>
              <SafeIcon icon={FiExternalLink} className="w-3 h-3" />
            </a>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Search Interface */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-2"
          >
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-center space-x-3 mb-6">
                <SafeIcon icon={FiMessageSquare} className="w-6 h-6 text-blue-600" />
                <h3 className="text-xl font-semibold text-gray-900">Natural Language Search</h3>
              </div>
              
              <div className="relative mb-6">
                <textarea
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Try: 'Show me a 2022+ Audi Q5 under 70,000 miles under $45K' or 'I need a reliable SUV for my family with good fuel economy and latest safety features'"
                  className="w-full p-4 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none resize-none h-24"
                />
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleSearch}
                  disabled={isSearching || !searchQuery.trim()}
                  className="absolute bottom-4 right-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <SafeIcon icon={FiSearch} className="w-4 h-4" />
                  <span>{isSearching ? 'Searching...' : 'Search'}</span>
                </motion.button>
              </div>

              {isSearching && (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">AI is analyzing your request...</span>
                </div>
              )}

              {aiRecommendation && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-lg"
                >
                  <h4 className="font-semibold text-blue-900 mb-2">AI Recommendation</h4>
                  <p className="text-blue-800 text-sm whitespace-pre-wrap">{aiRecommendation}</p>
                </motion.div>
              )}

              {searchResults && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="space-y-4"
                >
                  <h4 className="font-semibold text-gray-900 mb-4">Perfect Matches:</h4>
                  {searchResults.map((car) => (
                    <div key={car.id} className="border rounded-lg p-4 hover-lift">
                      <div className="flex space-x-4">
                        <img 
                          src={car.image} 
                          alt={`${car.year} ${car.make} ${car.model}`}
                          className="w-24 h-16 object-cover rounded"
                        />
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <h4 className="font-semibold text-gray-900">
                              {car.year} {car.make} {car.model}
                            </h4>
                            <span className="px-2 py-1 bg-green-100 text-green-600 text-xs rounded-full">
                              {car.aiMatch}% AI Match
                            </span>
                          </div>
                          <p className="text-gray-600 text-sm">{car.mileage.toLocaleString()} miles</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {car.features.slice(0, 3).map((feature, idx) => (
                              <span key={idx} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                {feature}
                              </span>
                            ))}
                          </div>
                          <p className="text-blue-600 font-semibold mt-2">${car.price.toLocaleString()}</p>
                        </div>
                        <div className="flex flex-col space-y-2">
                          <button className="px-4 py-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors text-sm">
                            Ask AI
                          </button>
                          <button className="px-4 py-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors text-sm">
                            Details
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </motion.div>
              )}
            </div>
          </motion.div>

          {/* AI Suggestions */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-6"
          >
            <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl p-6">
              <h4 className="font-semibold text-gray-900 mb-4">AI Suggestions</h4>
              <div className="space-y-3">
                <button className="w-full text-left p-3 bg-white rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiTrendingUp} className="w-4 h-4 text-blue-600" />
                    <span className="text-sm">Compare similar models</span>
                  </div>
                </button>
                <button className="w-full text-left p-3 bg-white rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiDollarSign} className="w-4 h-4 text-blue-600" />
                    <span className="text-sm">Get financing estimate</span>
                  </div>
                </button>
                <button className="w-full text-left p-3 bg-white rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiSearch} className="w-4 h-4 text-blue-600" />
                    <span className="text-sm">Market trend analysis</span>
                  </div>
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <h4 className="font-semibold text-gray-900 mb-4">Popular AI Searches</h4>
              <div className="space-y-2">
                {popularSearches.map((search, index) => (
                  <button
                    key={index}
                    onClick={() => setSearchQuery(search)}
                    className="w-full text-left p-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                  >
                    {search}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-xl p-6">
              <h4 className="font-semibold text-gray-900 mb-2">AI Advantage</h4>
              <p className="text-sm text-gray-600 mb-3">
                Our AI understands natural language and finds vehicles that match your exact needs, not just keywords.
              </p>
              <div className="text-xs text-gray-500">
                <p>• Semantic search understanding</p>
                <p>• Preference learning</p>
                <p>• Market analysis integration</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default AIVehicleFinder;